import { NdpsDealer } from '@esportal/portal-service';

import { fetchNdpsDomain } from './ndps-fetch';
import { arrayToObject } from '../helpers/array-utils';
import { configProviderManager } from './fetch-bff-data';

declare global {
	interface Window {
		google: any;
	}
}

declare const google: any;

export class DealersService {
	private dealers: NdpsDealer[] = [];
	private token: string;
	private domain: string;

	constructor(token?: string, domain?: string) {
		this.token = token || 'VW_DDB_FRM_LEM_REC_LEM';
		this.domain = domain || 'VOLKSWAGEN_SF_LEAD_INSTALLATION';
	}

	// Get dealers usando geoposicion navegador
	async getSortedDealersByCoordinates(lat: number, lon: number): Promise<NdpsDealer[]> {
		await this.fetchDealers();
		const dealersWithDistance = this.dealers
			.map((dealer) => {
				const distance =
					dealer.ShippingLatitude && dealer.ShippingLongitude
						? this.calculateDistance(
								lat,
								lon,
								parseFloat(dealer.ShippingLatitude),
								parseFloat(dealer.ShippingLongitude),
							)
						: Infinity;
				return { ...dealer, Distance: distance };
			})
			.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));

		return dealersWithDistance;
	}

	async getSortedDealers(input: string): Promise<NdpsDealer[]> {
		await this.fetchDealers();
		if (this.isPostalCode(input)) {
			return this.getPostalCodeDealersFirst(input);
		}
		if (/^\d+$/.test(input)) {
			return this.getPostalCodeDealersFirst(input);
		}
		return this.getLocationDealersFirst(input);
	}

	async fetchDealers(): Promise<void> {
		if (this.dealers.length === 0) {
			const response = await fetchNdpsDomain(this.token, this.domain);
			if (response) {
				const excludedNames = [
					'VENTAS MODELO AGENCIA',
					'CENT.TECN.SEAT-FLOTAS',
					'CENT.TECN.SEAT-ARRENDAMIENTO',
					'CENT.TCN.SEAT DOT.DIRECTIVOS',
					'CAMBIO DE NOMBRE COMERCIAL',
				];
				this.dealers = response.content
					.map((content) => arrayToObject(content.attributes) as NdpsDealer)
					.filter((dealer) => dealer.VGED_CommercialName__c && !excludedNames.includes(dealer.VGED_CommercialName__c));
			}
		}
	}

	async getSuggestions(input: string): Promise<string[]> {
		// Si el input es numérico y tiene 4+ dígitos, combinar Google Maps con códigos de dealers
		if (/^\d{4,}$/.test(input)) {
			await this.fetchDealers();
			const googleSuggestions = await this.getGooglePostalCodeSuggestions(input);
			const dealerSuggestions = this.getDealerPostalCodeSuggestions(input);

			// Combinar y filtrar duplicados, priorizando los validados por Google
			const combined = [...googleSuggestions];
			dealerSuggestions.forEach((code) => {
				if (!combined.includes(code)) {
					combined.push(code);
				}
			});

			return combined.slice(0, 10);
		}

		// Si el input es numérico pero tiene menos de 4 dígitos, no mostrar sugerencias
		if (/^\d+$/.test(input)) {
			return [];
		}

		// Para texto, necesita al menos 3 caracteres
		if (input.length < 3) {
			return [];
		}

		try {
			await this.loadGoogleMapsAPI();
			const service = new google.maps.places.AutocompleteService();

			const result = await new Promise<any[]>((resolve) => {
				service.getPlacePredictions(
					{
						input: input,
						componentRestrictions: { country: 'ES' },
						types: ['(cities)'],
					},
					(predictions: any[] | PromiseLike<any[]>, status: any) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
							resolve(predictions);
						} else {
							resolve([]);
						}
					},
				);
			});

			return result
				.filter((prediction) => {
					const types = prediction.types || [];
					return (
						types.includes('locality') ||
						types.includes('administrative_area_level_2') ||
						types.includes('administrative_area_level_1')
					);
				})
				.map((prediction) => prediction.description.split(',')[0])
				.slice(0, 5);
		} catch (error) {
			return [];
		}
	}

	private getDealerPostalCodeSuggestions(input: string): string[] {
		return this.dealers
			.map((dealer) => String(dealer.ShippingPostalCode))
			.filter((code) => code && code.startsWith(input))
			.filter((code, index, array) => array.indexOf(code) === index)
			.sort();
	}

	async getCoordinatesByPostalCode(postalCode: string): Promise<{ lat: number; lon: number } | null> {
		return this.getCoorsByPostalCode(postalCode);
	}

	// Ordena dealers priorizando coincidencias exactas de código postal y los demás por distancia
	private async getPostalCodeDealersFirst(postalCode: string): Promise<NdpsDealer[]> {
		// Buscar dealers con código postal exacto
		const exactMatches = this.dealers.filter((dealer) => {
			return String(dealer.ShippingPostalCode) === postalCode;
		});

		exactMatches.forEach((dealer) => {
			dealer.Distance = undefined;
		});

		// Obtener coordenadas del código postal
		const coords = await this.getCoorsByPostalCode(postalCode);

		// Si no hay dealers exactos y no hay coordenadas válidas, no devolver resultados
		if (exactMatches.length === 0 && (!coords || !this.isValidSpanishCoords(coords))) {
			return [];
		}

		// Resto de dealers ordenados por distancia
		const otherDealers = this.getOtherDealers(exactMatches, coords);

		return [...exactMatches, ...otherDealers];
	}

	// Ordena dealers priorizando coincidencias exactas de localidad o ciudad y los demás por distancia
	private async getLocationDealersFirst(location: string): Promise<NdpsDealer[]> {
		const normalizedLocation = this.normalizeText(location);
		let exactMatches = this.dealers.filter((dealer) => {
			if (!dealer.ShippingCity) return false;
			const normalizedCity = this.normalizeText(dealer.ShippingCity);
			return normalizedCity.startsWith(normalizedLocation);
		});

		if (exactMatches.length === 0) {
			exactMatches = this.dealers.filter((dealer) => {
				if (!dealer.Province) return false;
				const normalizedProvince = this.normalizeText(dealer.Province);
				return normalizedProvince.startsWith(normalizedLocation);
			});
		}

		// Ordenar exactMatches por distancia desde las coordenadas de Google Maps por Location
		if (exactMatches.length > 1) {
			const coords = await this.getCoordsByLocation(location);
			if (coords) {
				exactMatches.forEach((dealer) => {
					dealer.Distance =
						dealer.ShippingLatitude && dealer.ShippingLongitude
							? this.calculateDistance(
									coords.lat,
									coords.lon,
									parseFloat(dealer.ShippingLatitude),
									parseFloat(dealer.ShippingLongitude),
								)
							: undefined;
				});
				exactMatches.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));
			}
		}

		exactMatches.forEach((dealer) => {
			dealer.Distance = undefined;
		});

		const coords = await this.getCoordsByLocation(location);

		if (exactMatches.length === 0 && !coords) {
			return [];
		}

		// Resto de dealers ordenados por distancia y con nodo Distance calculado
		const otherDealers = this.getOtherDealers(exactMatches, coords);

		return [...exactMatches, ...otherDealers];
	}

	private getOtherDealers(exactMatches: NdpsDealer[], coords: { lat: number; lon: number } | null): NdpsDealer[] {
		const otherDealers = coords ? this.dealers.filter((dealer) => !exactMatches.includes(dealer)) : [];

		if (coords && otherDealers) {
			otherDealers.forEach((dealer) => {
				dealer.Distance =
					dealer.ShippingLatitude && dealer.ShippingLongitude
						? this.calculateDistance(
								coords.lat,
								coords.lon,
								parseFloat(dealer.ShippingLatitude),
								parseFloat(dealer.ShippingLongitude),
							)
						: undefined;
			});
			otherDealers.sort((a, b) => (a.Distance || Infinity) - (b.Distance || Infinity));
		}
		return otherDealers;
	}

	private async getGooglePostalCodeSuggestions(input: string): Promise<string[]> {
		try {
			await this.loadGoogleMapsAPI();
			const service = new google.maps.places.AutocompleteService();

			const result = await new Promise<any[]>((resolve) => {
				service.getPlacePredictions(
					{
						input: input,
						componentRestrictions: { country: 'ES' },
						types: ['postal_code'],
					},
					(predictions: any[] | PromiseLike<any[]>, status: any) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
							resolve(predictions);
						} else {
							resolve([]);
						}
					},
				);
			});

			const postalCodes = result
				.map((prediction) => {
					const description = prediction.description;
					// Extraer código postal de diferentes formatos
					const match = description.match(/\b\d{4,5}\b/);
					return match ? match[0] : null;
				})
				.filter((code) => code !== null)
				.slice(0, 10);

			return postalCodes;
		} catch (error) {
			console.error('Error getting postal code suggestions:', error);
			return [];
		}
	}

	// Obtiene coordenadas de código postal usando Google Maps
	private async getCoorsByPostalCode(postalCode: string): Promise<{ lat: number; lon: number } | null> {
		return await this.getGoogleCoords(postalCode);
	}

	// Obtiene coordenadas de ubicación usando Google Maps
	private async getCoordsByLocation(location: string): Promise<{ lat: number; lon: number } | null> {
		return await this.getGoogleCoords(location);
	}

	private async loadGoogleMapsAPI(): Promise<void> {
		if (typeof google !== 'undefined' && google.maps) {
			return;
		}

		return new Promise((resolve, reject) => {
			const apiKey = configProviderManager.getGoogleApiKey();
			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,places`;
			script.async = true;
			script.defer = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('Failed to load Google Maps API'));
			document.head.appendChild(script);
		});
	}

	private async getGoogleCoords(address: string): Promise<{ lat: number; lon: number } | null> {
		try {
			await this.loadGoogleMapsAPI();
			const geocoder = new google.maps.Geocoder();
			const result = await new Promise<any[]>((resolve) => {
				geocoder.geocode(
					{ address: `${address}, ES`, region: 'ES' },
					(results: any[] | PromiseLike<any[]>, status: string) => {
						if (status === 'OK' && results) {
							resolve(results);
						} else {
							resolve([]);
						}
					},
				);
			});

			if (result.length > 0) {
				const location = result[0].geometry.location;
				return {
					lat: location.lat(),
					lon: location.lng(),
				};
			}
		} catch (error) {
			// Error en geocodificación
		}
		return null;
	}

	// Comprueba si el texto es un código postal
	private isPostalCode(input: string): boolean {
		return /^\d{4,5}$/.test(input.trim());
	}

	// Verifica si las coordenadas están dentro de España (aproximadamente)
	private isValidSpanishCoords(coords: { lat: number; lon: number }): boolean {
		// Coordenadas aproximadas de España
		const minLat = 35.0; // Sur de España
		const maxLat = 44.0; // Norte de España
		const minLon = -10.0; // Oeste de España
		const maxLon = 5.0; // Este de España

		return coords.lat >= minLat && coords.lat <= maxLat && coords.lon >= minLon && coords.lon <= maxLon;
	}

	// Normaliza texto eliminando espacios, acentos y caracteres especiales para comparaciones
	private normalizeText(text: string): string {
		return text
			.toLowerCase()
			.replace(/\s+/g, '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/&#\d+;/g, '')
			.replace(/&[a-z]+;/g, '');
	}

	// Calcula la distancia en kilómetros entre dos puntos usando la fórmula de Haversine
	private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
		const R = 6371; // Radio de la Tierra en kilómetros
		const dLat = ((lat2 - lat1) * Math.PI) / 180;
		const dLon = ((lon2 - lon1) * Math.PI) / 180;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}
}
